import { useEffect, useState, useLayoutEffect } from 'react';
import styled from '@emotion/styled';
import {
  Flex,
  TopNavBar,
  VideoChatScreen,
  TabletVideoChatSidebar,
  theme,
  ResponsiveMenu,
  ResponsiveFloatingNotes,
} from '../../2022-component-library';
import { useMediaQuery } from '../../helpers/useMediaQuery';
import { useTestContext } from '../../context/testContext';
import { useCalibration } from '../../context/calibrationContext';
import { rulerDimensionCalculations } from '../../helpers/calibration-calculations';
import { ResponsiveContentOptician, ResponsiveRotateScreen } from '.';
import { useNavigationSync } from '../../context/navigationContext';
import { useReceiveMessage } from '../../context/messageContext';

const TabletViewContainer = styled(Flex)`
  justify-content: center;
  @media screen and (min-width: 1000px) {
    width: 744px;
  }

  @media screen and (min-width: 750px) and (min-height: 820px) {
    width: 744px;
    height: 744px;
    border-radius: 4px;
    box-shadow: ${theme.elevations.elevation1};
  }
`;

const ScreenLock = () => {
  return (
    <Flex
      centered
      style={{ position: 'absolute', top: 0, left: 0, zIndex: 900, width: '100vw', height: '100vh', overflow: 'hidden', backgroundColor: '#fff' }}
    >
      <ResponsiveRotateScreen orientation={'landscape'} />
    </Flex>
  );
};

const ResponsiveOpticianContainer = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [videoChatScreenOpen, setVideoChatScreenOpen] = useState(false);
  const showVideoSidebar = useMediaQuery('(orientation: landscape) and (min-width: 1000px)');
  const wrapInTabletView = useMediaQuery('(min-width: 700px) and (min-height: 700px)');
  const { setMultipleTestFieldValues, updateTestData } = useTestContext();
  const [title, setTitle] = useState('');
  const receivedMessage = useReceiveMessage();
  const [response, setResponse] = useState('');
  const { activeClinicianTab: activeTab, setActiveClinicianTab: setActiveTab } = useNavigationSync();
  const [calibrationStep, setCalibrationStep] = useState('screen-calibration-selection');
  const [patientResponse, setPatientResponse] = useState({});
  const [visualFieldUpdate, setVisualFieldUpdate] = useState({});
  const { updateCalibrationData } = useCalibration();
  const portraitView = useMediaQuery('(max-width: 700px) and (orientation: portrait)');
  const [showScreenLock, setShowScreenLock] = useState(false);
  const [showNotes, setShowNotes] = useState(false);

  const shouldBeLandscapeView = (tab) => {
    if (tab !== 'calibration-selection' && tab !== 'screen-calibration' && tab !== 'camera-calibration') return true;
  };

  useLayoutEffect(() => window.scrollTo(0, 0), [activeTab]);

  useEffect(() => {
    const checkForScreenLock = () => {
      if (portraitView && shouldBeLandscapeView(activeTab)) {
        setShowScreenLock(true);
      } else setShowScreenLock(false);
    };

    checkForScreenLock();
  }, [portraitView, activeTab]);

  useEffect(() => {
    if (showVideoSidebar) setVideoChatScreenOpen(false);
  }, [showVideoSidebar]);

  useEffect(() => {
    if (activeTab !== 'test') updateTestData({ testInProgress: false });
  }, [updateTestData, activeTab]);

  useEffect(() => {
    setCalibrationStep('screen-calibration-selection');
  }, [activeTab]);

  useEffect(() => {
    if (receivedMessage?.type === 'rulerDimensions') {
      const { rulerWidth, rulerHeight, screenWidthPx, screenHeightPx } = receivedMessage?.content;

      updateCalibrationData(rulerDimensionCalculations(rulerWidth, rulerHeight, screenWidthPx, screenHeightPx));
      setResponse('Patient has entered screen size');
    }

    if (receivedMessage?.type === 'patientMovementDetected') {
      const data = {
        patientDistance: receivedMessage?.content.patientDistance,
        patientIsMoving: receivedMessage?.content.patientIsMoving,
      };
      setPatientResponse({ ...patientResponse, data });
    }

    if (receivedMessage?.type === 'blindSpotUpdate') {
      setMultipleTestFieldValues(receivedMessage?.content);
      updateCalibrationData(receivedMessage?.content);
    }

    if (receivedMessage?.type === 'visualFieldUpdate') {
      setVisualFieldUpdate(receivedMessage?.content);
    }
  }, [receivedMessage]);

  const renderResponsiveOpticianContent = () => {
    return (
      <ResponsiveContentOptician
        setTitle={setTitle}
        title={title}
        calibrationStep={calibrationStep}
        setCalibrationStep={setCalibrationStep}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        response={response}
        setResponse={setResponse}
        patientResponse={patientResponse}
        visualFieldUpdate={visualFieldUpdate}
      />
    );
  };

  return (
    <>
      {showScreenLock && <ScreenLock />}
      {menuOpen && (
        <ResponsiveMenu
          setMenuOpen={setMenuOpen}
          setActiveTab={setActiveTab}
          setVideoChatScreenOpen={setVideoChatScreenOpen}
          tabletInPortrait={showVideoSidebar}
        />
      )}
      {!showVideoSidebar && <VideoChatScreen show={videoChatScreenOpen} />}
      {showNotes && <ResponsiveFloatingNotes hideNotes={() => setShowNotes(false)} />}
      <TopNavBar
        showNotes={showNotes}
        setShowNotes={setShowNotes}
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
        videoChatScreenOpen={videoChatScreenOpen}
        setVideoChatScreenOpen={setVideoChatScreenOpen}
        showVideoSidebar={showVideoSidebar}
      />
      <Flex
        justifyContent={showVideoSidebar ? 'flex-start' : 'center'}
        alignItems={wrapInTabletView ? 'center' : null}
        padding='0.5rem'
        width='100vw'
        height={`calc(${window.innerHeight}px - 55px)`}
        maxWidth={showScreenLock && '10vw'}
        maxHeight={showScreenLock && '10vh'}
      >
        {showVideoSidebar && <TabletVideoChatSidebar />}

        <TabletViewContainer>{renderResponsiveOpticianContent()}</TabletViewContainer>
      </Flex>
    </>
  );
};

export default ResponsiveOpticianContainer;
